import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Archive, Trash2, RotateCcw, Search, Loader, AlertTriangle, 
  Clock, ChevronDown, ChevronUp, Calendar, User, Building2
} from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import permissionsService from '../../services/permissionsService';

const ArchivedAssessments = ({ 
  isExpanded = false, 
  onToggle,
  onRefreshLibrary,
  asSection = true,
  refreshTrigger = 0
}) => {
  const { user } = useContext(AuthContext);
  const [archivedAssessments, setArchivedAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [permanentDeleting, setPermanentDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);

  const fetchArchivedAssessments = async () => {
    setLoading(true);
    try {
      const response = await AssessmentBuilderAPI.getArchivedAssessments({ search: searchTerm });
      setArchivedAssessments(response.results || []);
    } catch (error) {
      console.error('Error fetching archived assessments:', error);
      toast.error('Failed to load archived assessments');
    } finally {
      setLoading(false);
    }
  };

  // Check permissions and fetch count on mount
  useEffect(() => {
    const initializeSection = async () => {
      try {
        // Check delete permission
        const canDelete = await permissionsService.hasPermission(
          permissionsService.PERMISSIONS.DELETE_ASSESSMENT
        );
        setHasDeletePermission(canDelete || user?.is_superuser);
        
        // Fetch count to know if we should show the section
        const response = await AssessmentBuilderAPI.getArchivedAssessments({ limit: 1 });
        setArchivedCount(response.count || response.results?.length || 0);
      } catch (error) {
        console.error('Error initializing archived section:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    initializeSection();
  }, [user]);

  useEffect(() => {
    if (isExpanded || !asSection) {
      fetchArchivedAssessments();
    }
  }, [isExpanded, asSection]);

  // Refresh when parent triggers it (e.g., after delete)
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Always update count when triggered
      AssessmentBuilderAPI.getArchivedAssessments({ limit: 1 })
        .then(response => setArchivedCount(response.count || response.results?.length || 0))
        .catch(console.error);
      
      // Fetch full list if expanded
      if (isExpanded || !asSection) {
        fetchArchivedAssessments();
      }
    }
  }, [refreshTrigger, isExpanded, asSection]);

  useEffect(() => {
    if (isExpanded || !asSection) {
      const debounce = setTimeout(() => {
        fetchArchivedAssessments();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm]);

  const handleRestore = async (assessment) => {
    setRestoring(assessment.id);
    try {
      await AssessmentBuilderAPI.restoreAssessment(assessment.uuid);
      toast.success(`"${assessment.name}" has been restored`);
      fetchArchivedAssessments();
      if (onRefreshLibrary) onRefreshLibrary();
    } catch (error) {
      console.error('Error restoring assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to restore assessment');
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (assessment) => {
    setPermanentDeleting(assessment.id);
    try {
      await AssessmentBuilderAPI.permanentDeleteAssessment(assessment.uuid);
      toast.success(`"${assessment.name}" has been permanently deleted`);
      setConfirmDelete(null);
      fetchArchivedAssessments();
    } catch (error) {
      console.error('Error permanently deleting assessment:', error);
      toast.error(error.response?.data?.error || 'Failed to permanently delete assessment');
    } finally {
      setPermanentDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemainingColor = (days) => {
    if (days <= 7) return 'text-red-600 bg-red-50';
    if (days <= 30) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const content = (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search archived assessments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-200 focus:border-gray-400"
        />
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Archived assessments will be permanently deleted after 90 days. 
          Restore them to prevent automatic deletion.
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && archivedAssessments.length === 0 && (
        <div className="text-center py-8">
          <Archive className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No archived assessments</p>
          <p className="text-sm text-gray-400 mt-1">
            Deleted assessments will appear here for 90 days
          </p>
        </div>
      )}

      {/* Archived List */}
      {!loading && archivedAssessments.length > 0 && (
        <div className="space-y-3">
          {archivedAssessments.map((assessment) => (
            <div
              key={assessment.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{assessment.name}</h4>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {assessment.library_type_display}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      Deleted {formatDate(assessment.deleted_at)}
                    </span>
                    {assessment.deleted_by && (
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {assessment.deleted_by}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {/* Days Remaining Badge */}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getDaysRemainingColor(assessment.days_remaining)}`}>
                    <Clock className="w-3 h-3" />
                    {assessment.days_remaining} days left
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => handleRestore(assessment)}
                  disabled={restoring === assessment.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50"
                >
                  {restoring === assessment.id ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Restore
                </button>
                {user?.is_superuser && (
                  <button
                    onClick={() => setConfirmDelete(assessment)}
                    disabled={permanentDeleting === assessment.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    {permanentDeleting === assessment.id ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[70] p-4"
            onClick={() => !permanentDeleting && setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl dark:shadow-gray-900/20 max-w-md w-full p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Permanently Delete Assessment?</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    This will permanently delete <strong>"{confirmDelete.name}"</strong>. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setConfirmDelete(null)}
                  disabled={permanentDeleting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handlePermanentDelete(confirmDelete)}
                  disabled={permanentDeleting}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {permanentDeleting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete Permanently
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Hide section if still loading initial state
  if (initialLoading && asSection) {
    return null;
  }

  // Hide section if no archived content AND user doesn't have delete permission
  if (asSection && archivedCount === 0 && !hasDeletePermission) {
    return null;
  }

  // If used as a standalone page, return content directly
  if (!asSection) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <Archive className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Archived Assessments</h1>
            <p className="text-sm text-gray-500">
              Items will be permanently deleted after 90 days
            </p>
          </div>
        </div>
        {content}
      </div>
    );
  }

  // As a collapsible section
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div 
        className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-700 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <Archive className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Archived</h3>
            <p className="text-xs text-gray-500">Deleted assessments (90-day retention)</p>
          </div>
          {archivedAssessments.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 text-xs font-medium rounded-full">
              {archivedAssessments.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="p-5"
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArchivedAssessments;
